import React, {ReactNode, useState} from 'react';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import {useStreetSuggestions} from '../../../shared/components/street-suggestions';
import {displayPhone} from '../../../shared/components/list-page/list-format';
import {ActionPill, AddressLine, Divider} from '../../../shared/components/record/RecordKit';
import {ClientRatingDto} from './client-profile.service';
import {
  addressLine,
  addressParts,
  capitalizeWords,
  CONTACT_KINDS,
  ContactEntry,
  ContactKind,
  CreateQuestion,
  displayTitle,
  formatPhoneNumber,
  inSectionOrder,
  isMainKey,
  isPickable,
  isPicked,
  KIND_LABEL,
  mainEntry,
  ParsedAddress,
  phoneDigits,
} from './contact-entries';
import './ClientProfile.css';

/*
 * The client card, Android's ClientReadOnlyContent and ClientEditContent
 * (clients/ClientContent.kt): the rating, then the name, phone, address and
 * email, then Additional Client Info. Edit mode keeps view mode's lines, one
 * item per line in the same order, so nothing moves when Edit is pressed; the
 * only thing a wide card changes is the address, which takes one line.
 */

// ---- Rating and review --------------------------------------------------------------

const STAR_PATH = 'M12 3.2l2.62 5.62 5.88.78-4.3 4.1 1.1 5.94L12 16.8l-5.3 2.84 1.1-5.94-4.3-4.1 5.88-.78z';

/**
 * Five stars and a private review, at the top of the card. Not behind Edit:
 * rating a client is not editing their details. The review is never shown
 * once saved; only the link changes (Write review, Edit review).
 */
const ClientRating = ({rating, onStar, onSaveReview}: {
  rating: ClientRatingDto;
  onStar: (stars: number) => void;
  /** Resolves true once the review has landed, which closes the box. */
  onSaveReview: (text: string) => Promise<boolean>;
}) => {
  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const current = rating.rating ?? 0;
  return (
    <>
      <div className="cc-rating">
        <div className="cc-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" className="cc-star-button" onClick={() => onStar(n)}
                    aria-label={n === 1 ? '1 star' : `${n} stars`} aria-pressed={n <= current}>
              <svg className="cc-star" viewBox="0 0 24 24" aria-hidden="true">
                <path d={STAR_PATH} fill={n <= current ? 'var(--rk-star)' : 'none'}
                      stroke={n <= current ? 'var(--rk-star)' : 'var(--rk-ink-faint)'} strokeWidth={1.6} strokeLinejoin="round"/>
              </svg>
            </button>
          ))}
        </div>
        <span className="cc-rating-label">Client rating</span>
        {/* While the box is open its own Cancel / Save review close it. */}
        {!writing && (
          <button type="button" className="cc-review-link" onClick={() => { setDraft(rating.review ?? ''); setWriting(true); }}>
            {rating.review?.trim() ? 'Edit review' : 'Write review'}
          </button>
        )}
      </div>
      {writing && (
        <div className="cc-review">
          <textarea className="cc-review-box" rows={3} value={draft} placeholder="Type your review..." autoCapitalize="sentences"
                    aria-label="Review" onChange={e => setDraft(e.target.value)}/>
          <div className="cc-review-buttons">
            {/* Cancel restores whatever was last saved. */}
            <button type="button" className="cc-review-cancel" onClick={() => setWriting(false)}>Cancel</button>
            <button type="button" className="cc-review-save" disabled={saving} onClick={() => {
              setSaving(true);
              void onSaveReview(draft).then(saved => {
                setSaving(false);
                if (saved) setWriting(false);
              });
            }}>Save review</button>
          </div>
          <div className="cc-review-note">Private, only your team sees this</div>
        </div>
      )}
      <div className="rk-divider cc-rating-rule"/>
    </>
  );
};

// ---- The create question's checkbox -----------------------------------------------------

/** A checkbox beside a candidate, on the lists with a choice to make; anything else renders as it always does. */
const PickRow = ({question, entry, onPick, children}: {
  question: CreateQuestion;
  entry: ContactEntry;
  onPick: (entry: ContactEntry) => void;
  children: ReactNode;
}) => {
  if (!isPickable(question, entry.kind)) return <>{children}</>;
  const checked = isPicked(question, entry);
  return (
    <div className="cc-pick">
      <button type="button" className="cc-pick-box" role="checkbox" aria-checked={checked}
              aria-label={`Pick ${addressLine(entry) || entry.value || KIND_LABEL[entry.kind]}`} onClick={() => onPick(entry)}>
        {checked && <CheckIcon/>}
      </button>
      <div>{children}</div>
    </div>
  );
};

const AdditionalInfoHeader = ({open, onToggle}: {open: boolean; onToggle: () => void}) => (
  <>
    <div className="cc-more-rule"/>
    <button type="button" className="cc-more-head" onClick={onToggle} aria-expanded={open}>
      <span>Additional Client Info</span>
      {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
    </button>
  </>
);

interface CardProps {
  /** The client's fields and entries, and the create question when one is being asked. */
  question: CreateQuestion;
  onPick: (entry: ContactEntry) => void;
  /** Left out when the rating could not be read: the card then has none. */
  rating?: ClientRatingDto;
  onStar: (stars: number) => void;
  onSaveReview: (text: string) => Promise<boolean>;
  additionalOpen: boolean;
  onToggleAdditional: () => void;
  /**
   * The record's own address (an appointment's or estimate's job site), shown
   * on the address row in place of the client's: that is where the crew goes,
   * and Go opens it. Left out on the client's own page.
   */
  recordAddress?: ContactEntry;
  /**
   * The customer's view of a record (Preview, the customer's link; SQ-337):
   * the primary lines alone, with no Call, Go or Email and no Additional
   * Client Info.
   */
  customer?: boolean;
}

// ---- Saved ------------------------------------------------------------------------------

const phoneText = (value: string) => displayPhone(value) ?? value;

/** A saved entry: its title small and muted, the value right beneath it. */
const EntryReadOnly = ({entry}: {entry: ContactEntry}) => {
  const {street, rest} = addressParts(entry);
  return (
    <div className="cc-entry">
      {entry.title.trim() && <div className="cc-entry-title">{entry.title.trim()}</div>}
      <div className={entry.kind === 'name' ? 'cc-entry-value cc-entry-value--name' : 'cc-entry-value'}>
        {entry.kind === 'address' ? <AddressLine street={street || rest} rest={street ? rest : undefined}/>
          : entry.kind === 'phone' ? phoneText(entry.value)
            : entry.value}
      </div>
    </div>
  );
};

export const ClientCard = ({question, onPick, rating, onStar, onSaveReview, additionalOpen, onToggleAdditional, recordAddress, customer}: CardProps) => {
  const name = mainEntry(question.client, 'name');
  const phone = mainEntry(question.client, 'phone');
  const address = recordAddress ?? mainEntry(question.client, 'address');
  const email = mainEntry(question.client, 'email');
  const {street, rest} = addressParts(address);
  const entries = inSectionOrder(question.entries);
  const pick = (entry: ContactEntry, children: ReactNode) =>
    <PickRow question={question} entry={entry} onPick={onPick}>{children}</PickRow>;
  return (
    <div className="cc-frame">
      <div className="cc-card">
        {rating && <ClientRating rating={rating} onStar={onStar} onSaveReview={onSaveReview}/>}
        {pick(name, <div className="cc-name">{name.value}</div>)}
        <Divider/>
        {phone.value.trim() && <>
          {pick(phone, (
            <div className="cc-row">
              <div className="cc-row-text">{phoneText(phone.value)}</div>
              {!customer && <ActionPill href={`tel:${phone.value}`} icon={<PhoneIcon/>} label="Call"/>}
            </div>
          ))}
          <Divider/>
        </>}
        {(street || rest) && <>
          {pick(address, (
            <div className="cc-row">
              <div className="cc-row-text"><AddressLine street={street || rest} rest={street ? rest : undefined}/></div>
              {!customer && (
                <ActionPill external icon={<PlaceIcon/>} label="Go"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine(address))}`}/>
              )}
            </div>
          ))}
          <Divider/>
        </>}
        {email.value.trim() && pick(email, (
          <div className="cc-row">
            <div className="cc-row-text">{email.value}</div>
            {!customer && <ActionPill href={`mailto:${email.value}`} icon={<EmailIcon/>} label="Email"/>}
          </div>
        ))}
        {!customer && <AdditionalInfoHeader open={additionalOpen} onToggle={onToggleAdditional}/>}
        {!customer && additionalOpen && (entries.length === 0
          ? <div className="cc-more-empty">Nothing added</div>
          : (
            <div className="cc-entries">
              {entries.map((entry, index) => (
                <React.Fragment key={entry.key}>
                  {index > 0 && <Divider/>}
                  {pick(entry, <EntryReadOnly entry={entry}/>)}
                </React.Fragment>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

// ---- Edit: fields ---------------------------------------------------------------------------

/** Android's FloatingLabelField: an outlined box with its label sitting on the top border. */
const Field = ({label, value, onChange, strong, className, inputMode, autoFocus}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  strong?: boolean;
  className?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoFocus?: boolean;
}) => (
  <label className={className ? `cc-field ${className}` : 'cc-field'}>
    <span className="cc-label">{label}</span>
    <input className={strong ? 'cc-input cc-input--strong' : 'cc-input'} value={value} inputMode={inputMode}
           autoComplete="off" autoFocus={autoFocus} onChange={e => onChange(e.target.value)}/>
  </label>
);

/** The phone field: the mask fills in as it is typed, and a backspace over the mask's own characters takes a digit. */
const PhoneField = ({label, value, onChange}: {label: string; value: string; onChange: (value: string) => void}) => {
  const shown = formatPhoneNumber(value);
  return (
    <Field label={label} value={shown} inputMode="tel" onChange={typed => {
      const digits = phoneDigits(typed);
      const erasedMask = typed.length < shown.length && digits === phoneDigits(shown);
      onChange(formatPhoneNumber(erasedMask ? digits.slice(0, -1) : digits));
    }}/>
  );
};

/**
 * The street field with Google's address suggestions under it, as Android's
 * AddressAutocompleteField (useStreetSuggestions).
 */
const StreetField = ({label, value, onChange, onPicked}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPicked: (parsed: ParsedAddress) => void;
}) => {
  const {suggestions, active, search, pick, onBlur, onKeyDown} = useStreetSuggestions(onPicked);
  return (
    <div className="cc-street" onBlur={onBlur} onKeyDown={onKeyDown}>
      <Field label={label} value={value} onChange={typed => {
        const next = capitalizeWords(typed);
        onChange(next);
        search(next);
      }}/>
      {suggestions.length > 0 && (
        <ul className="cc-suggestions" role="listbox">
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.placeId} role="option" aria-selected={index === active}
                className={index === active ? 'cc-suggestion cc-suggestion--active' : 'cc-suggestion'}
                // The press, not the click: the field's blur would close the list under a click.
                onMouseDown={e => { e.preventDefault(); pick(suggestion); }}>
              {suggestion.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type EntryUpdate = (entry: ContactEntry) => ContactEntry;

/**
 * An address in edit mode. A phone: the street on its own line, then City |
 * State | Zip (2 : 1 : 1). A card 560px or wider: all four on one line, as
 * view mode's address is. `strict` is the entries' own limit (two letters,
 * five digits); the client's own state and zip are only uppercased.
 */
const AddressEditor = ({entry, label, strict, onChanged, onPicked}: {
  entry: ContactEntry;
  label: string;
  strict?: boolean;
  onChanged: (update: EntryUpdate) => void;
  onPicked: (parsed: ParsedAddress) => void;
}) => (
  <div className="cc-address">
    <StreetField label={label} value={entry.value} onChange={value => onChanged(e => ({...e, value}))} onPicked={onPicked}/>
    <Field label="City" value={entry.city} onChange={v => onChanged(e => ({...e, city: capitalizeWords(v)}))}/>
    <Field label="State" value={entry.state}
           onChange={v => onChanged(e => ({...e, state: strict ? v.toUpperCase().slice(0, 2) : v.toUpperCase()}))}/>
    <Field label="Zip" value={entry.postalCode} inputMode="numeric"
           onChange={v => onChanged(e => ({...e, postalCode: strict ? v.replace(/\D/g, '').slice(0, 5) : v}))}/>
  </div>
);

/**
 * The title input: opened in place, focused, with no placeholder (the link
 * that opened it already said what it is for). Done, Enter, or a click
 * anywhere else closes it; the title shows live on the field below.
 */
const TitleInput = ({value, onChange, onDone}: {value: string; onChange: (value: string) => void; onDone: () => void}) => (
  <span className="cc-title-box">
    <input className="cc-title-input" autoFocus value={value} aria-label="Title" autoComplete="off"
           onChange={e => onChange(e.target.value)} onBlur={onDone}
           // Enter only closes the input: it must never reach the page's Save.
           onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onDone(); } }}/>
    {/* The press must not blur the input first: it is the press that closes it. */}
    <button type="button" className="cc-title-done" onMouseDown={e => e.preventDefault()} onClick={onDone}>Done</button>
  </span>
);

/** An entry in edit mode: the "Add a title" / "Change title" link, then the card's own field with the title as its label. */
export const EntryEditor = ({entry, titling, onStartTitling, onStopTitling, onChanged, onTitleChanged, onPicked}: {
  entry: ContactEntry;
  titling: boolean;
  onStartTitling: () => void;
  onStopTitling: () => void;
  onChanged: (update: EntryUpdate) => void;
  onTitleChanged: (title: string) => void;
  onPicked: (parsed: ParsedAddress) => void;
}) => {
  const label = displayTitle(entry);
  return (
    <div className="cc-entry-editor">
      {isMainKey(entry.key) ? null
        : titling ? <TitleInput value={entry.title} onChange={onTitleChanged} onDone={onStopTitling}/>
          : <button type="button" className="cc-title-link" onClick={onStartTitling}>{entry.title.trim() ? 'Change title' : 'Add a title'}</button>}
      {entry.kind === 'address' ? <AddressEditor strict entry={entry} label={label} onChanged={onChanged} onPicked={onPicked}/>
        : entry.kind === 'phone' ? <PhoneField label={label} value={entry.value} onChange={value => onChanged(e => ({...e, value}))}/>
          : entry.kind === 'name' ? <Field label={label} value={entry.value} onChange={v => onChanged(e => ({...e, value: capitalizeWords(v)}))}/>
            : <Field label={label} value={entry.value} inputMode="email" onChange={value => onChanged(e => ({...e, value}))}/>}
    </div>
  );
};

/**
 * Edit mode's way to add an entry: a bordered box labelled "Add" with one button per list; each adds an empty entry.
 * A send block's offers only phones and emails (`kinds`) and sits on the page, not the card (`className`).
 */
export const AddEntryBox = ({onAdd, kinds = CONTACT_KINDS, className}: {
  onAdd: (kind: ContactKind) => void; kinds?: ContactKind[]; className?: string;
}) => (
  <div className={className ? `cc-add ${className}` : 'cc-add'}>
    <span className="cc-add-label">Add</span>
    {kinds.map(kind => (
      <button key={kind} type="button" className="cc-add-button" onClick={() => onAdd(kind)}>{KIND_LABEL[kind]}</button>
    ))}
  </div>
);

export interface EditCardProps extends CardProps {
  /** `key` is an entry's, or the client's own field's (`main-<kind>`). */
  onEntryChanged: (key: string, update: EntryUpdate) => void;
  onAddressPicked: (key: string, parsed: ParsedAddress) => void;
  onTitleChanged: (key: string, title: string) => void;
  onAddEntry: (kind: ContactKind) => void;
  /** With `recordAddress`: its edits and picks, which go to the record, never the client. */
  onRecordAddressChanged?: (update: EntryUpdate) => void;
  onRecordAddressPicked?: (parsed: ParsedAddress) => void;
}

export const ClientEditCard = ({
  question, onPick, rating, onStar, onSaveReview, additionalOpen, onToggleAdditional, recordAddress,
  onEntryChanged, onAddressPicked, onTitleChanged, onAddEntry, onRecordAddressChanged, onRecordAddressPicked,
}: EditCardProps) => {
  // Which entry's title input is open; one at a time.
  const [titlingKey, setTitlingKey] = useState<string | null>(null);
  const name = mainEntry(question.client, 'name');
  const phone = mainEntry(question.client, 'phone');
  const address = recordAddress ?? mainEntry(question.client, 'address');
  const changeAddress = (update: EntryUpdate) =>
    recordAddress ? onRecordAddressChanged?.(update) : onEntryChanged(address.key, update);
  const pickAddress = (parsed: ParsedAddress) =>
    recordAddress ? onRecordAddressPicked?.(parsed) : onAddressPicked(address.key, parsed);
  const email = mainEntry(question.client, 'email');
  const pick = (entry: ContactEntry, children: ReactNode) =>
    <PickRow question={question} entry={entry} onPick={onPick}>{children}</PickRow>;
  return (
    <div className="cc-frame">
      <div className="cc-card cc-card--edit">
        {rating && <ClientRating rating={rating} onStar={onStar} onSaveReview={onSaveReview}/>}
        <div className="cc-main">
          {pick(name, <Field strong label="Client Name" value={name.value}
                             onChange={v => onEntryChanged(name.key, e => ({...e, value: capitalizeWords(v)}))}/>)}
          <Divider/>
          {pick(phone, <PhoneField label="Phone" value={phone.value} onChange={value => onEntryChanged(phone.key, e => ({...e, value}))}/>)}
          <Divider/>
          {pick(address, <AddressEditor entry={address} label="Street Address" onChanged={changeAddress} onPicked={pickAddress}/>)}
          <Divider/>
          {pick(email, <Field label="Email" value={email.value} inputMode="email"
                              onChange={value => onEntryChanged(email.key, e => ({...e, value}))}/>)}
        </div>
        <AdditionalInfoHeader open={additionalOpen} onToggle={onToggleAdditional}/>
        {additionalOpen && <>
          <div className="cc-entries cc-entries--edit">
            {inSectionOrder(question.entries).map((entry, index) => (
              <React.Fragment key={entry.key}>
                {index > 0 && <Divider/>}
                {pick(entry, (
                  <EntryEditor
                    entry={entry}
                    titling={titlingKey === entry.key}
                    onStartTitling={() => setTitlingKey(entry.key)}
                    onStopTitling={() => setTitlingKey(key => key === entry.key ? null : key)}
                    onChanged={update => onEntryChanged(entry.key, update)}
                    onTitleChanged={title => onTitleChanged(entry.key, title)}
                    onPicked={parsed => onAddressPicked(entry.key, parsed)}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
          <AddEntryBox onAdd={onAddEntry}/>
        </>}
      </div>
    </div>
  );
};
